import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getCurrentUser, signOut, signIn } from 'aws-amplify/auth';
import { IS_PORTAL } from './config/auth';
import PortalApp from './portal/PortalApp';  // amos83 Request Portal — requester-only surface for /portal/*

import Layout from './components/Layout';
import RequireGroup from './components/RequireGroup';
import Dashboard from './pages/Dashboard';
import ClaimDetail from './pages/ClaimDetail';
import BdxGenerator from './pages/BdxGenerator';
import BdxCoverholder from './pages/BdxCoverholder';
import Analytics from './pages/Analytics';
import HealthMonitor from './pages/HealthMonitor';
import DiaryView from './pages/DiaryView';
import HandlersView from './pages/HandlersView';
import SupervisorDashboard from './pages/SupervisorDashboard';
import LossRunGenerator from './pages/LossRunGenerator';
import PrecedentSearch from './pages/PrecedentSearch';
import ConvexLossRun from './pages/ConvexLossRun';
import SlaReporting from './pages/SlaReporting';
import UserManagement from './pages/UserManagement';
import ProgramAdmin from './pages/ProgramAdmin';
import Guide from './pages/Guide';
import AdminDashboard from './pages/AdminDashboard';
import QueueTriage from './pages/QueueTriage';
import LoadingSpinner from './components/LoadingSpinner';

function LoginPage({ onLogin }: { onLogin: (email: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signIn({
        username: email,
        password: password,
        options: {
          authFlowType: 'USER_PASSWORD_AUTH',
        },
      });

      if (result.isSignedIn) {
        onLogin(email);
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setError('Password change required. Contact administrator.');
      } else {
        setError(`Unexpected auth step: ${result.nextStep?.signInStep}`);
      }
    } catch (err: any) {
      console.error('Sign-in error:', err);
      setError(err.message || 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-800">
            FileHandler Claims Intelligence
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            TransEleven Claims Management
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

function AuthenticatedApp({ userEmail, onLogout }: { userEmail: string; onLogout: () => Promise<void> }) {
  return (
    <Router>
      <Layout user={{ email: userEmail }} onLogout={onLogout}>
        <Routes>
          <Route path="/" element={<RequireGroup group="dashboard" moduleName="Dashboard"><Dashboard /></RequireGroup>} />
          <Route path="/claims/:id" element={<RequireGroup group="dashboard" moduleName="Claims"><ClaimDetail /></RequireGroup>} />
          <Route path="/analytics" element={<RequireGroup group="analytics" moduleName="Analytics"><Analytics /></RequireGroup>} />
          <Route path="/health" element={<RequireGroup group="health" moduleName="Health Monitor"><HealthMonitor /></RequireGroup>} />
          <Route path="/diary" element={<RequireGroup group="diary" moduleName="Diary"><DiaryView /></RequireGroup>} />
          <Route path="/handlers" element={<RequireGroup group="handlers" moduleName="Handlers"><HandlersView /></RequireGroup>} />
          <Route path="/bdx" element={<RequireGroup group="bdx-lloyds" moduleName="BDX Lloyd's"><BdxGenerator /></RequireGroup>} />
          <Route path="/bdx-coverholder" element={<RequireGroup group="bdx-billing" moduleName="BDX Billing"><BdxCoverholder /></RequireGroup>} />
          <Route path="/loss-runs" element={<RequireGroup group="loss-runs" moduleName="Loss Runs"><LossRunGenerator /></RequireGroup>} />
          <Route path="/changes" element={<RequireGroup group="changes" moduleName="Change Requests"><SupervisorDashboard /></RequireGroup>} />
          <Route path="/precedent-search" element={<RequireGroup group="precedent" moduleName="Precedent Search"><PrecedentSearch /></RequireGroup>} />
          <Route path="/convex/*" element={<RequireGroup group="convex-loss-run" moduleName="Convex Loss Run"><ConvexLossRun /></RequireGroup>} />
          <Route path="/sla-reporting" element={<RequireGroup group="sla-reporting" moduleName="SLA Reporting"><SlaReporting /></RequireGroup>} />
          <Route path="/users" element={<RequireGroup group="users" moduleName="User Management"><UserManagement /></RequireGroup>} />
          <Route path="/admin/programs" element={<RequireGroup group="programs" moduleName="Programs"><ProgramAdmin /></RequireGroup>} />
          <Route path="/guide" element={<RequireGroup group="guide" moduleName="Guide"><Guide /></RequireGroup>} />
          <Route path="/admin/queue-triage" element={<RequireGroup group="convex-loss-run" moduleName="Queue Triage"><QueueTriage /></RequireGroup>} />
          <Route path="/admin" element={<RequireGroup group="admin" moduleName="Admin"><AdminDashboard /></RequireGroup>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </Router>
  );
}

function App() {
  // amos83: /portal/* is the requester pool + portal shell only; every other path is unchanged.
  if (IS_PORTAL) return <PortalApp />;
  return <StaffApp />;
}

function StaffApp() {
  const [user, setUser] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    getCurrentUser()
      .then((u) => {
        setUser(u.signInDetails?.loginId || u.username || 'user');
        setChecking(false);
      })
      .catch(() => setChecking(false));
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Sign-out error:', err);
    } finally {
      setUser(null);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLogin={(email) => setUser(email)} />;
  }

  return <AuthenticatedApp userEmail={user} onLogout={handleLogout} />;
}

export default App;
